import React from 'react';
import iconUser     from '../../assets/user.svg';
import iconUsers    from '../../assets/users.svg';
import iconUsersAlt from '../../assets/users-alt.svg';
import { formatEUR } from './constants';

const VentilationPanel = ({
    mode, setMode, availableModes,
    orderItems, remainingAmount, unpaidItems,
    splitCount, setSplitCount,
    splitBaseAmount, splitPaymentsMade,
    freeAmount, setFreeAmount,
    partialQty, setPartialQty,
}) => {
    const getSplitIcon = () => {
        if (splitCount <= 1) return iconUser;
        if (splitCount === 2) return iconUsers;
        return iconUsersAlt;
    };

    const splitDisplayAmount = () => {
        if (splitBaseAmount != null) {
            const paymentsLeft = splitCount - splitPaymentsMade;
            return paymentsLeft <= 1 ? remainingAmount : splitBaseAmount;
        }
        return Math.round(remainingAmount / splitCount * 100) / 100;
    };

    // Stepper live pour le mode "Par article" — met à jour partialQty immédiatement
    const setPartialItemQty = (id, delta) => {
        setPartialQty(prev => {
            const item   = unpaidItems.find(i => i.id === id);
            const maxQty = item?.qty ?? 0;
            const current = prev[id] ?? 0;
            const next    = Math.max(0, Math.min(maxQty, current + delta));
            return { ...prev, [id]: next };
        });
    };

    const partialTotal = unpaidItems.reduce((sum, item) => {
        const qty = partialQty[item.id] ?? 0;
        return sum + item.price * qty;
    }, 0);

    const renderContent = () => {
        switch (mode) {

            case 'total':
                return (
                    <div className="vent-card dark-card">
                        <span className="vent-big-label">TOUT RÉGLER</span>
                        <span className="vent-huge-price">{formatEUR(remainingAmount)}</span>
                        <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
                            {orderItems.length} article{orderItems.length > 1 ? 's' : ''} inclus
                        </p>
                    </div>
                );

            case 'split':
                return (
                    <div className="vent-card dark-card">
                        {splitBaseAmount != null && (
                            <span className="split-lock-badge">Montant verrouillé</span>
                        )}
                        <div className="split-controls">
                            <button
                                className="big-stepper-btn"
                                onClick={() => setSplitCount(Math.max(2, splitCount - 1))}
                                disabled={splitBaseAmount != null}
                            >&minus;</button>
                            <div className="split-display">
                                <span className="huge-number">{splitCount}</span>
                                <img
                                    src={getSplitIcon()} alt="" className="white_svg"
                                    style={{ width: 28, height: 28, opacity: 0.8 }}
                                />
                            </div>
                            <button
                                className="big-stepper-btn"
                                onClick={() => setSplitCount(splitCount + 1)}
                                disabled={splitBaseAmount != null}
                            >&#43;</button>
                        </div>
                        <div className="split-result">
                            <span className="split-label">Soit par personne :</span>
                            <span className="split-amount">{formatEUR(splitDisplayAmount())}</span>
                        </div>
                        {splitPaymentsMade > 0 && (
                            <p className="text-muted" style={{ fontSize: 12, margin: 0 }}>
                                {splitPaymentsMade} / {splitCount} part{splitPaymentsMade > 1 ? 's' : ''} réglée{splitPaymentsMade > 1 ? 's' : ''}
                            </p>
                        )}
                    </div>
                );

            case 'partial':
                if (unpaidItems.length === 0) {
                    return (
                        <div className="vent-card dark-card">
                            <span className="vent-big-label" style={{ color: '#4fabf7' }}>
                                Tous les articles ont été réglés
                            </span>
                        </div>
                    );
                }
                return (
                    <div className="dark-card partial-list">
                        {/* Sous-total live en haut */}
                        <div className="partial-subtotal">
                            <span className="text-muted" style={{ fontSize: 12 }}>Sélection</span>
                            <span className="partial-subtotal-amount">{formatEUR(partialTotal)}</span>
                        </div>

                        {unpaidItems.map(item => {
                            const selectedQty = partialQty[item.id] ?? 0;
                            const isActive    = selectedQty > 0;
                            return (
                                <div
                                    key={item.id}
                                    className={`ledger-item${isActive ? ' ledger-item--active' : ''}`}
                                >
                                    <div className="item-stepper">
                                        <button onClick={() => setPartialItemQty(item.id, -1)}>
                                            &minus;
                                        </button>
                                        <span>{selectedQty}</span>
                                        <button onClick={() => setPartialItemQty(item.id, +1)}>
                                            &#43;
                                        </button>
                                    </div>
                                    <div className="ledger-item-details">
                                        <span className="item-name">{item.name}</span>
                                        <span className="text-muted" style={{ fontSize: 12 }}>
                                            {item.qty} restant{item.qty > 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <span className="item-total-price">
                                        {formatEUR(item.price * selectedQty)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                );

            case 'free':
                return (
                    <div className="vent-card dark-card">
                        <span className="vent-big-label">MONTANT LIBRE</span>
                        <div className="free-input-row">
                            <input
                                type="number"
                                className="huge-input"
                                placeholder="0.00"
                                value={freeAmount}
                                onChange={e => setFreeAmount(e.target.value)}
                                min="0"
                                step="0.01"
                                autoFocus
                            />
                            <span className="currency-suffix">€</span>
                        </div>
                        {parseFloat(freeAmount) > remainingAmount && (
                            <p className="vent-warning">
                                Dépassement — sera limité à {formatEUR(remainingAmount)}
                            </p>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="flex-col" style={{ gap: 10, overflow: 'hidden', minHeight: 0 }}>
            <div className="ventilation-tabs">
                {availableModes.map(m => (
                    <button
                        key={m.key}
                        className={mode === m.key ? 'active' : ''}
                        onClick={() => setMode(m.key)}
                    >
                        {m.label}
                    </button>
                ))}
            </div>

            <div className="ventilation-content-area">
                {renderContent()}
            </div>

            <div className="vent-footer">
                <span className="text-muted" style={{ fontSize: 13 }}>Reste à payer</span>
                <span className="vent-footer-amount">{formatEUR(remainingAmount)}</span>
            </div>
        </div>
    );
};

export default VentilationPanel;